import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as google_protobuf_empty_pb from 'google-protobuf/google/protobuf/empty_pb';

import { HealthzResponse, RequestDto } from '../proto/generated/inv_common_message_pb';
import {
  AdjustmentNoteListResponse,
  GetListByContractRequest,
  GetLowStockWarningsRequest,
  GetLowStockWarningsResponse,
  GetNonSerializedInventoryListRequest,
  GetNonSerializedInventoryByNameRequest,
  LowStockWarning,
  NonSerializedInventoryAdjustRequest,
  NonSerializedInventoryDataSourceResponse,
  NonSerializedInventoryListResponse,
  NonSerializedInventoryTransferRequest,
  NonSerializedInventoryRequest,
  NonSerializedInventoryResponse,
} from '../proto/generated/inv_non_serialized_inventory_service_pb';
import {
  AddOrUpdateQuantityRequest,
  GetByLocationIdRequest,
  GetByNonSerializedInventoryIdRequest,
  NonSerializedInventoryLocationListResponse,
  NonSerializedInventoryLocationResponse,
} from '../proto/generated/inv_non_serialized_inventory_message_pb';
import { NonSerializedInventoryServiceClient } from '../proto/generated/inv_non_serialized_inventory_service_pb_service';

import {
  LowStockWarningModel,
  LowStockWarningsResult,
  NonSerializedAdjustmentNoteModel,
  NonSerializedInventoryDataSourceModel,
  NonSerializedInventoryLocationModel,
  NonSerializedInventoryModel,
  NonSerializedInventoryTransferModel,
} from '../shared/models';
import { BaseGRRCService } from '../shared/services/base.service';

@Injectable({ providedIn: 'root' })
export class NonSerializedInventoryGRPCService extends BaseGRRCService<NonSerializedInventoryServiceClient> {
  constructor() {
    super(NonSerializedInventoryServiceClient);
    // this.client = new NonSerializedInventoryServiceClient('https://groupmanagementui.devcloud.olameter.com/backend');

    this.getApplicationBEVersion()
      .then((data: any) => {})
      .catch((reason) => {
        this.showConnectionError(
          'NOTIFICATIONS.SERVICES.NON_SERIALIZED_INVENTORY',
          reason
        );
      });
  }

  public healthz(): Observable<HealthzResponse> {
    const request = new google_protobuf_empty_pb.Empty();

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<HealthzResponse>((observer) => {
        this.client.healthz(request, requestOptions, (error, response) => {
          if (error) {
            this.handleError(error);
            observer.error(error);
            return;
          }

          if (response) {
            observer.next(response);
            observer.complete();
          }
        });
      });
    });
  }

  public getListByContract(name: string): Observable<NonSerializedInventoryModel[]> {
    const request = new GetListByContractRequest();
    request.setContractName(name);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel[]>((observer) => {
        this.client.getListByContract(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryListResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const inventories = response.getNonSerializedInventoriesList();
              observer.next(NonSerializedInventoryModel.MapperList(inventories));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public getLowStockWarnings(contractName: string): Observable<LowStockWarningsResult> {
    const request = new GetLowStockWarningsRequest();
    request.setContractName(contractName || '');

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<LowStockWarningsResult>((observer) => {
        this.client.getLowStockWarnings(
          request,
          requestOptions,
          (error, response: GetLowStockWarningsResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              observer.next({
                issueCount: response.getIssueCount(),
                warnings: this.mapLowStockWarnings(response.getWarningsList()),
              });
              observer.complete();
            }
          }
        );
      });
    });
  }

  public getAll(): Observable<NonSerializedInventoryModel[]> {
    const request = new google_protobuf_empty_pb.Empty();

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel[]>((observer) => {
        this.client.getAll(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryListResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const inventories = response.getNonSerializedInventoriesList();
              observer.next(NonSerializedInventoryModel.MapperList(inventories));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public getList(filter: RequestDto): Observable<NonSerializedInventoryDataSourceModel> {
    const request = new GetNonSerializedInventoryListRequest();
    request.setFilter(filter);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryDataSourceModel>((observer) => {
        this.client.getList(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryDataSourceResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              observer.next(NonSerializedInventoryDataSourceModel.Mapper(response));
              observer.complete();
            }
          }
        );
      });
    });
  }
  

  public getByName(name: string): Observable<NonSerializedInventoryModel> {
    const request = new GetNonSerializedInventoryByNameRequest();
    request.setName(name);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel>((observer) => {
        this.client.getByName(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const inventory = response.getNonSerializedInventory();
              observer.next(NonSerializedInventoryModel.Mapper(inventory));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public create(
    inventory: NonSerializedInventoryModel
  ): Observable<NonSerializedInventoryModel> {
    const request = this.createInventoryRequest(inventory);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel>((observer) => {
        this.client.create(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const mappedInventory = response.getNonSerializedInventory();
              observer.next(NonSerializedInventoryModel.Mapper(mappedInventory));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public update(
    inventory: NonSerializedInventoryModel
  ): Observable<NonSerializedInventoryModel> {
    const request = this.createInventoryRequest(inventory);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel>((observer) => {
        this.client.update(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const mappedInventory = response.getNonSerializedInventory();
              observer.next(NonSerializedInventoryModel.Mapper(mappedInventory));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public transfer(
    transfer: NonSerializedInventoryTransferModel
  ): Observable<NonSerializedInventoryModel[]> {
    const request = new NonSerializedInventoryTransferRequest();
    request.setTransfer(NonSerializedInventoryTransferModel.MapperProto(transfer));
  
    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel[]>((observer) => {
        this.client.transfer(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryListResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const mappedInventories =
                response.getNonSerializedInventoriesList();
              observer.next(NonSerializedInventoryModel.MapperList(mappedInventories));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public adjust(
    nonSerializedInventoryName: string,
    locationName: string,
    adjustmentNote: NonSerializedAdjustmentNoteModel
  ): Observable<NonSerializedInventoryModel> {
    const request = new NonSerializedInventoryAdjustRequest();
    request.setNonSerializedInventoryName(nonSerializedInventoryName);
    request.setLocationName(locationName);
    request.setAdjustmentNote(
      NonSerializedAdjustmentNoteModel.MapperProto(adjustmentNote)
    );

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel>((observer) => {
        this.client.adjust(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const mappedInventory = response.getNonSerializedInventory();
              observer.next(NonSerializedInventoryModel.Mapper(mappedInventory));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public delete(
    inventory: NonSerializedInventoryModel
  ): Observable<NonSerializedInventoryModel> {
    const request = this.createInventoryRequest(inventory);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryModel>((observer) => {
        this.client.delete(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const mappedInventory = response.getNonSerializedInventory();
              observer.next(NonSerializedInventoryModel.Mapper(mappedInventory));
              observer.complete();
            }
          }
        );
      });
    });
  }

  public getLocationsByInventoryId(
    nonSerializedInventoryId: number
  ): Observable<NonSerializedInventoryLocationModel[]> {
    const request = new GetByNonSerializedInventoryIdRequest();
    request.setNonSerializedInventoryId(nonSerializedInventoryId);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryLocationModel[]>(
        (observer) => {
          this.client.getLocationsByInventoryId(
            request,
            requestOptions,
            (error, response: NonSerializedInventoryLocationListResponse) => {
              if (error) {
                this.handleError(error);
                observer.error(error);
                return;
              }

              if (response) {
                const locations = response.getNonSerializedInventoryLocationsList();
                observer.next(
                  NonSerializedInventoryLocationModel.MapperList(locations)
                );
                observer.complete();
              }
            }
          );
        }
      );
    });
  }

  public getLocationsByLocationId(
    locationId: number
  ): Observable<NonSerializedInventoryLocationModel[]> {
    const request = new GetByLocationIdRequest();
    request.setLocationId(locationId);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryLocationModel[]>(
        (observer) => {
          this.client.getLocationsByLocationId(
            request,
            requestOptions,
            (error, response: NonSerializedInventoryLocationListResponse) => {
              if (error) {
                this.handleError(error);
                observer.error(error);
                return;
              }

              if (response) {
                const locations = response.getNonSerializedInventoryLocationsList();
                observer.next(
                  NonSerializedInventoryLocationModel.MapperList(locations)
                );
                observer.complete();
              }
            }
          );
        }
      );
    });
  }

  public addOrUpdateQuantity(
    nonSerializedInventoryId: number,
    locationId: number,
    quantity: number
  ): Observable<NonSerializedInventoryLocationModel> {
    const request = new AddOrUpdateQuantityRequest();
    request.setNonSerializedInventoryId(nonSerializedInventoryId);
    request.setLocationId(locationId);
    request.setQuantity(quantity);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedInventoryLocationModel>((observer) => {
        this.client.addOrUpdateQuantity(
          request,
          requestOptions,
          (error, response: NonSerializedInventoryLocationResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const location = response.getNonSerializedInventoryLocation();
              observer.next(NonSerializedInventoryLocationModel.Mapper(location));
            }
            observer.complete();
          }
        );
      });
    });
  }

  public getAdjustmentNotes(name: string): Observable<NonSerializedAdjustmentNoteModel[]> {
    const request = new GetNonSerializedInventoryByNameRequest();
    request.setName(name);

    return this.withGrpcOptions((requestOptions) => {
      return new Observable<NonSerializedAdjustmentNoteModel[]>((observer) => {
        this.client.getAdjustmentNotes(
          request,
          requestOptions,
          (error, response: AdjustmentNoteListResponse) => {
            if (error) {
              this.handleError(error);
              observer.error(error);
              return;
            }

            if (response) {
              const adjustmentNotes = response.getAdjustmentNotesList();
              observer.next(NonSerializedAdjustmentNoteModel.MapperList(adjustmentNotes));
              observer.complete();
            }
          }
        );
      });
    });
  }

  private createInventoryRequest(
    inventory: NonSerializedInventoryModel
  ): NonSerializedInventoryRequest {
    const request = new NonSerializedInventoryRequest();
    request.setNonSerializedInventory(
      NonSerializedInventoryModel.MapperProto(inventory)
    );
    return request;
  }

  private mapLowStockWarnings(warnings: LowStockWarning[]): LowStockWarningModel[] {
    return warnings.map((warning) => ({
      warningKey: warning.getWarningKey(),
      nonSerializedInventoryName: warning.getNonSerializedInventoryName(),
      item: warning.getItem(),
      countRemaining: warning.getCountRemaining(),
      threshold: warning.getThreshold(),
    }));
  }
}
